import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowRight } from "lucide-react";
import { scrollToSection } from "@/components/Header";

const LINES = ["ONE PIECE", "MAINTENANCE"];

const lineVariants = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 },
  }),
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const metaOpacity = useTransform(scrollYProgress, [0, 0.4], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen border-b border-[#111111]/15 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{ backgroundImage: "linear-gradient(#11111110 1px, transparent 1px), linear-gradient(90deg, #11111110 1px, transparent 1px)", backgroundSize: "72px 72px" }}
      />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-0 min-h-screen">
        <div className="lg:col-span-7 flex flex-col justify-center lg:pr-16">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            data-testid="hero-eyebrow"
            className="flex items-center gap-3 text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-[#003BFF] mb-8"
          >
            <span className="w-10 h-[2px] bg-[#003BFF]" />
            Licensed Plumber · Melbourne's North & Surrounds
          </motion.p>

          <h1 className="font-display uppercase leading-[0.9] tracking-wide text-[13vw] sm:text-[10vw] lg:text-[5.5rem] xl:text-[6.5rem]">
            {LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                  className={`block ${i === 1 ? "text-[#003BFF]" : ""}`}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            data-testid="hero-subtext"
            className="mt-8 max-w-md text-base md:text-lg leading-relaxed text-[#555555] font-medium"
          >
            Expert maintenance plumbing across Melbourne's north — proper
            diagnostics, the right repairs, and your property left in One Piece.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-quote-button"
              onClick={() => scrollToSection("#contact")}
              className="group bg-[#003BFF] text-white rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] flex items-center gap-2 hover:-translate-y-1 transition-transform duration-300"
            >
              Request a Free Quote
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-services-button"
              onClick={() => scrollToSection("#services")}
              className="group rounded-full px-8 py-4 text-sm font-bold uppercase tracking-[0.15em] border border-[#111111]/25 hover:border-[#111111] hover:-translate-y-1 transition-[border-color,transform] duration-300 flex items-center gap-2"
            >
              Our Services
              <ArrowDownRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5" />
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative lg:border-l lg:border-[#111111]/15 lg:pl-10 flex items-center">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1], delay: 0.6 }}
            className="w-full"
          >
            <motion.div
              style={{ y: imageY }}
              data-testid="hero-image-placeholder"
              data-image-slot="hero"
              className="relative bg-neutral-300 w-full aspect-[3/4] flex items-center justify-center overflow-hidden"
            >
              <img
                src="/assets/hero-work.webp"
                alt="One Piece Maintenance plumber installing a bath"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <span className="absolute bottom-4 left-4 z-10 bg-[#111111]/80 text-white text-[10px] font-bold uppercase tracking-[0.25em] px-3 py-2">
                Bath Install — Melbourne's North
              </span>
              <span className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-[#111111]/40" />
              <span className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-[#111111]/40" />
              <span className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-[#111111]/40" />
              <span className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-[#111111]/40" />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.div
        style={{ opacity: metaOpacity }}
        className="absolute bottom-0 left-0 right-0 border-t border-[#111111]/15 hidden md:block"
      >
        <div className="max-w-[1440px] mx-auto px-12 grid grid-cols-3 divide-x divide-[#111111]/15">
          {[
            ["Est. 2022", "Melbourne's north & surrounds"],
            ["Upfront Pricing", "Transparency with every customer"],
            ["Proper Diagnostics", "The right repair, first time"],
          ].map(([title, sub]) => (
            <div key={title} className="py-5 pr-8">
              <p className="text-xs font-bold uppercase tracking-[0.2em]">{title}</p>
              <p className="text-xs text-[#555555] mt-1">{sub}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
